/**
 * Public byline safety.
 *
 * Article rows can inherit an author name from the staff account that created
 * the draft (which may be an email address or a phone number). None of that
 * belongs on a public page or in structured data, so every public surface runs
 * the stored value through `publicAuthorName` first.
 */

export const DEFAULT_AUTHOR_NAME = "TruWe Clinical Library";

const EMAIL_RE = /[^\s@]+@[^\s@]+/;
const PHONE_RE = /(?:\+?\d[\d\s().-]{6,})/;

/** True when the stored name leaks personal contact details. */
export function isContactLikeName(name: string): boolean {
  const v = name.trim();
  if (!v) return true;
  if (EMAIL_RE.test(v)) return true;
  if (PHONE_RE.test(v)) return true;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 7;
}

/** Display-safe byline: never an email address or phone number. */
export function publicAuthorName(name?: string | null): string {
  if (!name) return DEFAULT_AUTHOR_NAME;
  return isContactLikeName(name) ? DEFAULT_AUTHOR_NAME : name.trim();
}

/** Only keep an author profile URL when it isn't a mailto:/tel: link. */
export function publicAuthorUrl(url?: string | null): string | null {
  if (!url) return null;
  const v = url.trim();
  if (!v) return null;
  if (/^(mailto:|tel:|sms:|whatsapp:)/i.test(v)) return null;
  return v;
}

/**
 * Person names (reviewers) must never be an email address or a phone number.
 * Unlike the author byline there is no safe fallback, so the credit is dropped.
 */
export function publicPersonName(name?: string | null): string | null {
  if (!name) return null;
  const v = name.trim();
  if (!v || isContactLikeName(v)) return null;
  return v;
}
